import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

const MOBILE_STICKY_QUERY = window.matchMedia('(max-width: 749px)');

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #cartReceiveStartTimeout;

  /** @type {number | undefined} */
  #cartPulseTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #viewportFrame = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {HTMLElement | null} */
  #productInformation = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupViewportTracking();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.refs.stickyBar.addEventListener('click', this.#handleStickyBarClick, { signal });
    window.addEventListener('scroll', this.#queueViewportUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#queueViewportUpdate, { signal });

    this.#getInitialQuantity();
    this.#queueViewportUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#viewportFrame) {
      cancelAnimationFrame(this.#viewportFrame);
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#cartReceiveStartTimeout) {
      clearTimeout(this.#cartReceiveStartTimeout);
    }
    if (this.#cartPulseTimeout) {
      clearTimeout(this.#cartPulseTimeout);
    }
  }

  /**
   * Caches the page elements needed to control sticky bar visibility.
   */
  #setupViewportTracking() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#buyButtonsBlock = productForm.closest('.buy-buttons-block');
    this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    this.#productInformation = productForm.closest('.product-information');
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  #queueViewportUpdate = () => {
    if (this.#viewportFrame) return;

    this.#viewportFrame = window.requestAnimationFrame(() => {
      this.#viewportFrame = null;
      this.#updateStickyVisibility();
    });
  };

  #updateStickyVisibility() {
    if (!this.#buyButtonsBlock || !this.#footer) {
      this.#hideStickyBar();
      return;
    }

    const footerRect = this.#footer.getBoundingClientRect();
    const buyButtonsRect = this.#buyButtonsBlock.getBoundingClientRect();
    const productRect = this.#productInformation?.getBoundingClientRect() || null;
    const footerEntering = footerRect.top <= window.innerHeight - 96;

    let shouldShow = false;

    if (MOBILE_STICKY_QUERY.matches) {
      shouldShow = buyButtonsRect.bottom < 0 && !footerEntering;
    } else {
      const productTop = productRect ? productRect.top + window.scrollY : 0;
      const showAfter = productTop + Math.min(window.innerHeight * 0.78, 720);
      const productStillVisible = productRect ? productRect.bottom > window.innerHeight * 0.24 : true;
      shouldShow = window.scrollY > showAfter && productStillVisible && !footerEntering;
    }

    if (shouldShow) {
      this.#showStickyBar();
      return;
    }

    this.#hideStickyBar();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#cartReceiveStartTimeout) clearTimeout(this.#cartReceiveStartTimeout);
    if (this.#cartPulseTimeout) clearTimeout(this.#cartPulseTimeout);

    cartIcon.classList.remove('cart-icon--receiving');
    this.#cartReceiveStartTimeout = window.setTimeout(() => {
      cartIcon.classList.add('cart-icon--receiving');
      this.#cartPulseTimeout = window.setTimeout(() => {
        cartIcon.classList.remove('cart-icon--receiving');
      }, 320);
    }, 55);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 960);
  };

  #handleStickyBarClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    if (!target || target.closest('.sticky-add-to-cart__button')) return;

    const scrollTarget = this.#getPurchaseScrollTarget();
    if (!scrollTarget) return;

    event.preventDefault();
    const header = document.querySelector('#header-group, .shopify-section-group-header-group');
    const headerOffset = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
    const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - headerOffset - 12;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  };

  /**
   * Finds the real purchase area connected to the product form.
   * @returns {HTMLElement | null}
   */
  #getPurchaseScrollTarget() {
    if (!this.#buyButtonsBlock) return null;

    const bundleSelector = this.#buyButtonsBlock.querySelector('.ch-bundle-selector');
    if (bundleSelector instanceof HTMLElement) {
      return bundleSelector;
    }

    let sibling = this.#buyButtonsBlock.previousElementSibling;
    while (sibling) {
      if (sibling instanceof HTMLElement && sibling.matches('variant-picker, .variant-picker')) {
        return sibling;
      }
      sibling = sibling.previousElementSibling;
    }

    return this.#buyButtonsBlock;
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#queueViewportUpdate();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

const averoakProductMediaStickyUrl = new URL('./averoak-product-media-sticky.js', import.meta.url);
import(averoakProductMediaStickyUrl.href).catch(() => {});
